import { useEffect, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import {
  Button,
  Title,
  Checkbox,
  TextInput,
  Select,
  Switch,
  Textarea,
  Stack,
  Text,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useNavigate, useParams } from "react-router-dom";
import * as countryCodes from "country-codes-list";
import { useForm } from "@tanstack/react-form";

import { CREATE_GROUP, UPDATE_USER_GROUP } from "../../gql/groups";

import {
  STYLE,
  GroupType,
  CarrierType,
  PHONE_NUMBER_DEFAULT_VALUE,
  ThreeDModelPricingType,
} from "@jasper/shared";
import { UseAuth } from "/@/contexts";
import { AddressForm, PhoneNumberInput, validateSchema } from "@jasper/forms";
import { Address, AddressSchema, PricingTypeEnum } from "@jasper/client-models";
import {
  CheckBipConfigurationDocument,
  GetUserGroupsWhereDocument,
  GetUserGroupsWhereQuery,
  GetUserGroupsWhereUniqueDocument,
  GetUserGroupsWhereUniqueQuery,
  UserGroupStatus,
  UserGroupType,
} from "../../generated/graphql";
import { openConfirmModal } from "@mantine/modals";
import { isValidPhoneNumber } from "libphonenumber-js";

const GroupNew = () => {
  const [createGroup] = useMutation(CREATE_GROUP);
  const [updateGroupMutation] = useMutation(UPDATE_USER_GROUP);
  const navigate = useNavigate();
  const { group_id } = useParams();
  const { user } = UseAuth();

  const [providerList, setProviderList] = useState<
    { id: string; name: string; activated: boolean }[]
  >([]);

  const { data: groupInfo } = useQuery(GetUserGroupsWhereUniqueDocument, {
    variables: {
      where: {
        id: group_id,
      },
    },
    fetchPolicy: "network-only",
  });

  const { data: allProviders } = useQuery(GetUserGroupsWhereDocument, {
    variables: {
      where: {
        groupType: {
          equals: UserGroupType.Supplier,
        },
      },
    },
  });

  const [checkBipConfiguration] = useLazyQuery(CheckBipConfigurationDocument);

  const defaultValues = {
    name: "",
    groupType: "",
    status: UserGroupStatus.Active,
    description: "",
    phoneNumber: "",

    carrier: {
      pickupCarrier: "",
      deliveryCarrier: "",
    },

    billing: {
      pricingType: "",
      threeDModelPricingType: "",
      exemptOriginalAbutmentCharge: false,
      hidePricesOnPractitionerTraceability: true,
    },

    address: {
      country: countryCodes.findOne("countryCode", "FR")?.countryNameEn || "",
      addressName: "",
      streetNumber: "",
      streetName: "",
      city: "",
      postalCode: "",
      details: "",
    } as Partial<Address>,
  };

  const promptBipValidation = async ({
    clinicNumber,
  }: {
    clinicNumber?: string;
  }) => {
    // No clinic Number means the group is not created yet, so we know for sure that it is not configured for BIP has it requires the clinicNumber to be configured. So we can skip the check and directly prompt the user.
    if (clinicNumber) {
      const { data } = await checkBipConfiguration({
        variables: {
          clinicNumber,
        },
      });

      if (data?.checkBipConfiguration) {
        return true;
      }
    }

    return new Promise(resolve => {
      openConfirmModal({
        title: "BIP Configuration",
        children: (
          <Text>
            This group is not configured for BIP. Label won&apos;t be generated
            for this group until it is configured. Are you sure you want to
            continue?
          </Text>
        ),
        labels: { confirm: "OK", cancel: "Cancel" },
        onConfirm: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
  };

  const form = useForm({
    onSubmit: async data => {
      await data.formApi.validateAllFields("submit");
      const { value } = data;
      const newProviders = providerList.filter(p => p.activated).map(p => p.id);
      const isDentistGroup = value.groupType === UserGroupType.Dentist;

      const isCreation = !groupInfo?.getUserGroupsWhereUnique?.id;
      const isPickupCarrierChanged =
        groupInfo?.getUserGroupsWhereUnique?.pickupCarrier !==
        value.carrier.pickupCarrier;
      const isDeliveryCarrierChanged =
        groupInfo?.getUserGroupsWhereUnique?.deliveryCarrier !==
        value.carrier.deliveryCarrier;

      const pickupCarrier = value.carrier.pickupCarrier;
      const deliveryCarrier = value.carrier.deliveryCarrier;

      if (
        (deliveryCarrier === CarrierType.BIP &&
          (isDeliveryCarrierChanged || isCreation)) ||
        (pickupCarrier === CarrierType.BIP &&
          (isPickupCarrierChanged || isCreation))
      ) {
        const isValidatedByUser = await promptBipValidation({
          clinicNumber:
            groupInfo?.getUserGroupsWhereUnique?.clinicNumber ?? undefined,
        });
        if (!isValidatedByUser) {
          return;
        }
      }

      if (!isCreation) {
        updateGroup(
          value,
          groupInfo?.getUserGroupsWhereUnique,
          newProviders,
          isDentistGroup
        );
      } else {
        createNewGroup(value, newProviders, isDentistGroup);
      }
    },
    defaultValues,
  });

  useEffect(() => {
    if (!groupInfo?.getUserGroupsWhereUnique) {
      return;
    }

    const userGroup = groupInfo?.getUserGroupsWhereUnique;
    const phoneNumber = userGroup.phoneNumber;
    const groupAddress =
      (userGroup.addresses ?? []).length > 0 ? userGroup.addresses?.[0] : null;

    if (userGroup.name) {
      form.setFieldValue("name", userGroup.name);
    }

    if (userGroup.status) {
      form.setFieldValue("status", userGroup.status);
    }

    if (userGroup.groupType) {
      form.setFieldValue("groupType", userGroup.groupType);
    }

    if (userGroup.description) {
      form.setFieldValue("description", userGroup.description);
    }

    form.setFieldValue(
      "phoneNumber",
      phoneNumber === "NOT_PROVIDED" ? "" : phoneNumber
    );

    form.setFieldValue("carrier.pickupCarrier", userGroup.pickupCarrier);
    form.setFieldValue("carrier.deliveryCarrier", userGroup.deliveryCarrier);

    if (groupAddress) {
      if (groupAddress.country)
        form.setFieldValue("address.country", groupAddress.country);
      form.setFieldValue("address.addressName", groupAddress.name);
      form.setFieldValue("address.streetNumber", groupAddress.streetNumber!);
      form.setFieldValue("address.streetName", groupAddress.streetName);
      form.setFieldValue("address.city", groupAddress.city);
      form.setFieldValue("address.postalCode", groupAddress.postcode);
      form.setFieldValue("address.details", groupAddress.details ?? undefined);
    }

    if (userGroup.clientBilling?.pricingType) {
      form.setFieldValue(
        "billing.pricingType",
        userGroup.clientBilling?.pricingType
      );
    }

    form.setFieldValue(
      "billing.threeDModelPricingType",
      userGroup.threeDModelPricingType
    );
    if (userGroup.exemptOriginalAbutmentCharge !== undefined) {
      form.setFieldValue(
        "billing.exemptOriginalAbutmentCharge",
        userGroup.exemptOriginalAbutmentCharge
      );
    }
    if (userGroup.hidePricesOnPractitionerTraceability !== undefined) {
      form.setFieldValue(
        "billing.hidePricesOnPractitionerTraceability",
        userGroup.hidePricesOnPractitionerTraceability
      );
    }
  }, [groupInfo?.getUserGroupsWhereUnique]);

  const updateGroup = async (
    data: typeof defaultValues,
    existingGroup?: GetUserGroupsWhereUniqueQuery["getUserGroupsWhereUnique"],
    providersIds?: string[],
    isDentistGroup?: boolean
  ) => {
    try {
      await updateGroupMutation({
        variables: {
          where: {
            id: existingGroup?.id,
          },
          providerGroupIds: providersIds,
          data: {
            name: {
              set: data.name,
            },
            groupType: {
              set: data.groupType,
            },
            status: {
              set: data.status,
            },
            pickupCarrier: isDentistGroup
              ? {
                  set: data.carrier.pickupCarrier,
                }
              : undefined,
            deliveryCarrier: isDentistGroup
              ? {
                  set: data.carrier.deliveryCarrier,
                }
              : undefined,
            phoneNumber: {
              set: data.phoneNumber
                ? data.phoneNumber
                : PHONE_NUMBER_DEFAULT_VALUE,
            },
            description: {
              set: data.description,
            },
            threeDModelPricingType: isDentistGroup
              ? {
                  set: data.billing.threeDModelPricingType,
                }
              : undefined,
            exemptOriginalAbutmentCharge: isDentistGroup
              ? {
                  set: data.billing.exemptOriginalAbutmentCharge,
                }
              : undefined,
            hidePricesOnPractitionerTraceability: isDentistGroup
              ? {
                  set: data.billing.hidePricesOnPractitionerTraceability,
                }
              : undefined,
            clientBilling: existingGroup?.clientBilling?.id
              ? {
                  update: {
                    where: {
                      id: {
                        equals: existingGroup.clientBilling.id,
                      },
                    },
                    data: {
                      pricingType: {
                        set: data.billing.pricingType,
                      },
                    },
                  },
                }
              : data.groupType === GroupType.DENTIST
                ? {
                    create: {
                      pricingType: data.billing.pricingType,
                    },
                  }
                : undefined,
            addresses: isDentistGroup
              ? {
                  update: [
                    {
                      where: {
                        id:
                          (existingGroup?.addresses ?? []).length > 0
                            ? existingGroup?.addresses?.[0]?.id
                            : undefined,
                      },
                      data: {
                        city: {
                          set: data.address.city,
                        },
                        country: {
                          set: data.address.country,
                        },
                        name: {
                          set: data.address.addressName,
                        },
                        postcode: {
                          set: data.address.postalCode,
                        },
                        streetName: {
                          set: data.address.streetName,
                        },
                        streetNumber: {
                          set: data.address.streetNumber,
                        },
                        details: {
                          set: data.address.details ?? null,
                        },
                      },
                    },
                  ],
                }
              : undefined,
          },
        },
      });
      navigate(`/groups/${groupInfo?.getUserGroupsWhereUnique?.id}`);
    } catch (e) {
      console.error(e);
      notifications.show({
        title: "Error while trying to update group",
        color: "red",
        message: "",
      });
    }
  };

  const createNewGroup = async (
    data: typeof defaultValues,
    providerIds: string[],
    isDentistGroup: boolean
  ) => {
    try {
      const req = await createGroup({
        variables: {
          providerGroupIds: providerIds,
          args: {
            name: data.name,
            groupType: data.groupType,
            status: data.status,
            pickupCarrier: isDentistGroup
              ? data.carrier.pickupCarrier
              : undefined,
            deliveryCarrier: isDentistGroup
              ? data.carrier.deliveryCarrier
              : undefined,
            phoneNumber: data.phoneNumber
              ? data.phoneNumber
              : PHONE_NUMBER_DEFAULT_VALUE,
            description: data.description,
            threeDModelPricingType: isDentistGroup
              ? data.billing.threeDModelPricingType
              : undefined,
            exemptOriginalAbutmentCharge: isDentistGroup
              ? data.billing.exemptOriginalAbutmentCharge
              : undefined,
            hidePricesOnPractitionerTraceability: isDentistGroup
              ? data.billing.hidePricesOnPractitionerTraceability
              : undefined,
            clientBilling:
              data.groupType === GroupType.DENTIST
                ? {
                    create: {
                      pricingType: data.billing.pricingType,
                    },
                  }
                : undefined,
            addresses: isDentistGroup
              ? {
                  create: [
                    {
                      city: data.address.city,
                      country: data.address.country,
                      name: data.address.addressName,
                      postcode: data.address.postalCode,
                      streetName: data.address.streetName,
                      streetNumber: data.address.streetNumber,
                      details: data.address.details,
                    },
                  ],
                }
              : undefined,
          },
        },
      });
      navigate(`/groups/${req?.data?.createUserGroup?.id}`);
    } catch (e) {
      console.error(e);
      notifications.show({
        title:
          "Error while trying to create a new group, this name may already exist",
        color: "red",
        message: "",
      });
    }
  };

  const displayProviders = (
    allProviders: GetUserGroupsWhereQuery["getUserGroupsWhere"],
    availableProviders: GetUserGroupsWhereUniqueQuery["getUserGroupsWhereUnique"]["availableProvider"]
  ) => {
    return (allProviders ?? []).map(provider => {
      return {
        id: provider.id,
        name: provider.name,
        activated: !!(availableProviders ?? []).find(
          ap => ap?.provider?.id === provider?.id
        ),
      };
    });
  };

  useEffect(() => {
    setProviderList(
      displayProviders(
        allProviders?.getUserGroupsWhere ?? [],
        groupInfo?.getUserGroupsWhereUnique?.availableProvider ?? []
      ) ?? []
    );
  }, [
    allProviders?.getUserGroupsWhere,
    groupInfo?.getUserGroupsWhereUnique?.availableProvider,
  ]);

  return (
    <Stack gap="md">
      <Stack gap="sm">
        <Title fw={200}>General info</Title>
        <form.Field
          name="name"
          validators={{
            onChange: ({ value }) => {
              if (value && value.length > 27) {
                return "Group name must be at most 27 characters";
              }
            },
            onSubmit: ({ value }) => {
              if (!value) {
                return "Group name is required";
              }

              if (value.length > 27) {
                return "Group name must be at most 27 characters";
              }
            },
          }}
        >
          {field => {
            return (
              <TextInput
                label="Group name"
                name="name"
                required
                value={field.state.value}
                error={field.state.meta.errors[0]}
                onBlur={field.handleBlur}
                onChange={e => field.setValue(e.target.value)}
              />
            );
          }}
        </form.Field>
        <form.Field
          name="groupType"
          validators={{
            onChange: ({ value }) => {
              if (value && !Object.keys(GroupType).includes(value)) {
                return "Group type is invalid";
              }
            },
            onSubmit: ({ value }) => {
              if (!value) {
                return "Group type is required";
              }

              if (!Object.keys(GroupType).includes(value)) {
                return "Group type is invalid";
              }
            },
          }}
        >
          {field => {
            return (
              <Select
                label="Group type"
                name="groupType"
                required
                value={field.state.value}
                error={field.state.meta.errors[0]}
                data={Object.keys(GroupType)}
                onBlur={field.handleBlur}
                onChange={value => value && field.setValue(value)}
              />
            );
          }}
        </form.Field>
        <form.Field
          name="status"
          validators={{
            onSubmit: ({ value }) => {
              if (!value) {
                return "Group status is required";
              }
            },
          }}
        >
          {field => {
            return (
              <Select
                label="Group status"
                name="status"
                required
                value={field.state.value}
                error={field.state.meta.errors[0]}
                data={Object.keys(UserGroupStatus).map(status =>
                  status.toUpperCase()
                )}
                onBlur={field.handleBlur}
                onChange={value => value && field.setValue(value)}
              />
            );
          }}
        </form.Field>
        <form.Field
          name="phoneNumber"
          validators={{
            onBlur: ({ value }) => {
              if (!value) {
                return;
              }

              if (!isValidPhoneNumber(value)) {
                return "This field must be a valid phone number";
              }
            },
            onSubmit: ({ value }) => {
              if (!value) {
                return;
              }

              if (!isValidPhoneNumber(value)) {
                return "This field must be a valid phone number";
              }
            },
          }}
        >
          {field => {
            return (
              <PhoneNumberInput
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={value => field.setValue(value ?? undefined)}
                label="Phone Number"
                error={field.state.meta.errors[0]}
              />
            );
          }}
        </form.Field>
        <form.Field name="description">
          {field => {
            return (
              <Textarea
                label="Description"
                name="description"
                placeholder="Note about this clinic, cameras and so on..."
                value={field.state.value}
                error={field.state.meta.errors[0]}
                onBlur={field.handleBlur}
                onChange={e => field.setValue(e.target.value)}
              />
            );
          }}
        </form.Field>
      </Stack>
      <form.Subscribe selector={state => [state.values.groupType]}>
        {([groupType]) => {
          if (groupType !== GroupType.DENTIST) {
            return null;
          }

          return (
            <>
              <Stack gap="sm">
                <Title fw={200}>Carrier</Title>
                <form.Field
                  name="carrier.pickupCarrier"
                  validators={{
                    onChange: ({ value }) => {
                      if (value && !Object.keys(CarrierType).includes(value)) {
                        return "Pickup carrier is invalid";
                      }
                    },
                    onSubmit: ({ value }) => {
                      if (!value) {
                        return "Pickup carrier is required";
                      }

                      if (!Object.keys(CarrierType).includes(value)) {
                        return "Pickup carrier is invalid";
                      }
                    },
                  }}
                >
                  {field => {
                    return (
                      <Select
                        label="Pickup carrier"
                        name="carrier.pickupCarrier"
                        required
                        value={field.state.value}
                        error={field.state.meta.errors[0]}
                        data={Object.keys(CarrierType)}
                        onBlur={field.handleBlur}
                        onChange={value => value && field.setValue(value)}
                      />
                    );
                  }}
                </form.Field>
                <form.Field
                  name="carrier.deliveryCarrier"
                  validators={{
                    onChange: ({ value }) => {
                      if (value && !Object.keys(CarrierType).includes(value)) {
                        return "Delivery carrier is invalid";
                      }
                    },
                    onSubmit: ({ value }) => {
                      if (!value) {
                        return "Delivery carrier is required";
                      }

                      if (!Object.keys(CarrierType).includes(value)) {
                        return "Delivery carrier is invalid";
                      }
                    },
                  }}
                >
                  {field => {
                    return (
                      <Select
                        label="Delivery carrier"
                        name="carrier.deliveryCarrier"
                        required
                        value={field.state.value}
                        error={field.state.meta.errors[0]}
                        data={Object.keys(CarrierType)}
                        onBlur={field.handleBlur}
                        onChange={value => value && field.setValue(value)}
                      />
                    );
                  }}
                </form.Field>
              </Stack>

              <Stack gap="sm">
                <Title fw={200}>Billing</Title>
                <form.Field
                  name="billing.pricingType"
                  validators={{
                    onChange: ({ value }) => {
                      if (
                        value &&
                        !Object.keys(PricingTypeEnum).includes(value)
                      ) {
                        return "Pricing type is invalid";
                      }
                    },
                    onSubmit: ({ value }) => {
                      if (!value) {
                        return "Pricing type is required";
                      }

                      if (!Object.keys(PricingTypeEnum).includes(value)) {
                        return "Pricing type is invalid";
                      }
                    },
                  }}
                >
                  {field => {
                    return (
                      <Select
                        label="Pricing type"
                        name="billing.pricingType"
                        required
                        value={field.state.value}
                        error={field.state.meta.errors[0]}
                        data={Object.keys(PricingTypeEnum)}
                        onBlur={field.handleBlur}
                        onChange={value => value && field.setValue(value)}
                      />
                    );
                  }}
                </form.Field>
                <form.Field
                  name="billing.threeDModelPricingType"
                  validators={{
                    onChange: ({ value }) => {
                      if (
                        value &&
                        !Object.keys(ThreeDModelPricingType).includes(value)
                      ) {
                        return "3D Model Pricing type is invalid";
                      }
                    },
                    onSubmit: ({ value }) => {
                      if (!value) {
                        return "3D Model Pricing type is required";
                      }

                      if (
                        !Object.keys(ThreeDModelPricingType).includes(value)
                      ) {
                        return "3D Model Pricing type is invalid";
                      }
                    },
                  }}
                >
                  {field => {
                    return (
                      <Select
                        label="3D Model Pricing"
                        name="billing.threeDModelPricingType"
                        required
                        value={field.state.value}
                        error={field.state.meta.errors[0]}
                        data={[
                          {
                            value: ThreeDModelPricingType.FULL,
                            label: "Full Price (10€)",
                          },
                          {
                            value: ThreeDModelPricingType.HALF,
                            label: "Half Price (5€)",
                          },
                          {
                            value: ThreeDModelPricingType.FREE,
                            label: "Free (0€)",
                          },
                        ]}
                        onBlur={field.handleBlur}
                        onChange={value => value && field.setValue(value)}
                      />
                    );
                  }}
                </form.Field>

                <form.Field name="billing.exemptOriginalAbutmentCharge">
                  {field => {
                    return (
                      <Switch
                        label="Offer original abutment surcharge for free"
                        name="billing.exemptOriginalAbutmentCharge"
                        checked={field.state.value}
                        error={field.state.meta.errors[0]}
                        onBlur={field.handleBlur}
                        onChange={e => field.setValue(e.target.checked)}
                      />
                    );
                  }}
                </form.Field>

                <form.Field name="billing.hidePricesOnPractitionerTraceability">
                  {field => {
                    return (
                      <Switch
                        label="Hide prices on dentist traceability sheet"
                        name="billing.hidePricesOnPractitionerTraceability"
                        checked={field.state.value}
                        error={field.state.meta.errors[0]}
                        onBlur={field.handleBlur}
                        onChange={e => field.setValue(e.target.checked)}
                      />
                    );
                  }}
                </form.Field>
              </Stack>
              <Stack gap="sm">
                <Title fw={200} style={{ paddingBottom: "1rem" }}>
                  Address
                </Title>
                <form.Field
                  name="address"
                  validators={{
                    onBlur: ({ value }) => {
                      const result = validateSchema(AddressSchema, value);
                      if (!result.success) {
                        return result.errors;
                      }
                    },
                    onSubmit: ({ value }) => {
                      const result = validateSchema(AddressSchema, value);
                      if (!result.success) {
                        return result.errors;
                      }
                    },
                  }}
                >
                  {field => {
                    return (
                      <AddressForm
                        value={field.state.value}
                        errors={field.state.meta.errors[0]}
                        onBlur={() => field.handleBlur()}
                        onChange={value => {
                          field.setValue(value);
                        }}
                      />
                    );
                  }}
                </form.Field>
              </Stack>

              {user?.allowedToEditProviderAccess && (
                <Stack gap="sm">
                  <Title fw={200} style={{ paddingBottom: "1rem" }}>
                    Allowed providers
                  </Title>
                  {providerList.map(item => {
                    return (
                      <Checkbox
                        key={item.id}
                        label={item.name}
                        style={{ paddingBottom: "0.2rem" }}
                        checked={item.activated}
                        onChange={e => {
                          setProviderList(state =>
                            state.map(s => {
                              if (s.id === item.id) {
                                return { ...s, activated: e.target.checked };
                              }
                              return s;
                            })
                          );
                        }}
                      />
                    );
                  })}
                </Stack>
              )}
            </>
          );
        }}
      </form.Subscribe>
      <div style={{ textAlign: "center", marginTop: "2rem" }}>
        <Button
          style={{ backgroundColor: STYLE.primary }}
          onClick={() => {
            form.handleSubmit();
          }}
        >
          Save
        </Button>
      </div>
    </Stack>
  );
};

export default GroupNew;
